import { ReactNode, Suspense, useEffect, useRef } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { Box, Column } from "@hightouch/htui";
import { Navigate, NoContextOutlet, useLocation } from "src/router";

import { Nav } from "src/components/layout/nav/nav";
import { TimingMark } from "src/components/layout/timing-mark";
import { useIsAgenticSurfaceActive } from "src/hooks/use-agentic-home-enabled";
import { useUser } from "src/contexts/user-context";
import { SOFT_PAGE_BACKGROUND } from "src/ui/agentic-surfaces";
import { PageContentSkeleton } from "src/components/loading";
import { ErrorBoundary } from "src/components/error-boundary";
import { ImpersonationOverlay } from "src/components/layout/impersonation-overlay";
import { SpaceAccessGuard } from "src/components/spaces/space-access-guard";
import { AiFeaturesAnnouncementDialog } from "src/components/modals/ai-features-announcement-dialog";
import { ScopedAgentsTermsOfService } from "src/pages/agents/components/agents-tos";

export const Layout = ({ children = null }: { children?: ReactNode }) => {
  const { user, isLoading, isCompanyRequired } = useUser();
  const location = useLocation();
  const isAgenticSurfaceActive = useIsAgenticSurfaceActive();

  const navTimingFired = useRef(false);

  useEffect(() => {
    if (user && !navTimingFired.current) {
      navTimingFired.current = true;
      datadogRum.addTiming("nav_rendered");
    }
  }, [user]);

  if (isLoading) {
    return <PageContentSkeleton />;
  }

  if (!user) {
    return (
      <Navigate
        state={location?.state}
        to={`/login?returnTo=${encodeURI(location?.pathname)}`}
      />
    );
  }

  if (isCompanyRequired) {
    return (
      <Navigate
        state={location?.state}
        to={`/restricted?returnTo=${encodeURI(location?.pathname)}`}
      />
    );
  }

  return (
    <>
      <ImpersonationOverlay />
      {user && <Nav />}
      <Column bg="base.background" flex={1} minWidth={0}>
        {/* Agentic surfaces paint this gradient from a lazily-loaded chunk;
            painting it here too keeps the transparent nav from ever having the
            white document body behind it while that chunk loads. */}
        {isAgenticSurfaceActive && (
          <Box
            aria-hidden
            position="fixed"
            inset={0}
            zIndex={0}
            pointerEvents="none"
            background={SOFT_PAGE_BACKGROUND}
          />
        )}
        <ErrorBoundary>
          <Suspense fallback={<PageContentSkeleton />}>
            <TimingMark name="route_chunk_loaded" />
            <SpaceAccessGuard>
              <NoContextOutlet />
              {children}
              <ScopedAgentsTermsOfService />
              <AiFeaturesAnnouncementDialog />
            </SpaceAccessGuard>
          </Suspense>
        </ErrorBoundary>
      </Column>
    </>
  );
};
